/**
 * Formata os dados vindos da API comparativa para serem consumidos pelos gráficos.
 *
 * @param {Array<Object>} apiData Dados brutos da API.
 * @param {Object} intl Instância do useIntl() para formatação de strings internacionalizadas.
 * @returns {Array<Object>} Array formatado para os gráficos.
 */
export function formatGroupComparisonData(apiData, intl) {
    if (!apiData || !Array.isArray(apiData)) return [];

    return apiData.map(group => {
        let fallbackName = `Grupo ${group.group_id}`;
        if (intl) {
            fallbackName = intl.formatMessage(
                {
                    id:
                        'page.robots.cleaning_report.group_comparison.group_name_fallback',
                },
                { id: group.group_id }
            );
        }
        return {
            name: group.group_name || fallbackName,
            tasks: group.tasks || 0,
            time: group.time_seconds
                ? Number((group.time_seconds / 3600).toFixed(2))
                : 0,
            area: group.area_sqm || 0,
            frequency: group.frequency || 0,
        };
    });
}

export function calculateTotals(formattedData) {
    if (!formattedData || !Array.isArray(formattedData)) {
        return { tasks: 0, time: 0, area: 0, frequency: 0 };
    }

    return formattedData.reduce(
        (acc, curr) => {
            acc.tasks += curr.tasks;
            acc.time += curr.time;
            acc.area += curr.area;
            acc.frequency += curr.frequency;
            return acc;
        },
        { tasks: 0, time: 0, area: 0, frequency: 0 }
    );
}

/**
 * Converte a lista de grupos em dados compatíveis com o gráfico de barras.
 * Itera sobre a lista de grupos do SAAS e mapeia as métricas dos relatórios de limpeza para exibição.
 *
 * @param {Array} list Lista de tarefas.
 * @param {Array} cc1Robots Lista de robôs.
 * @param {Array} saasGroups Lista de grupos do SAAS.
 * @param {Object} intl Instância do useIntl() para formatação de strings internacionalizadas.
 * @returns {Array<Object>} Array de objetos de métricas comparativas por grupo.
 */
export function buildGroupComparisonDataFromList(
    list,
    cc1Robots,
    saasGroups = [],
    intl
) {
    if (!list || !Array.isArray(list) || !cc1Robots) return [];

    const robotToGroup = {};
    const validGroupIds = new Set();
    cc1Robots.forEach(r => {
        if (r && r.id && r.group_id) {
            robotToGroup[r.id] = r.group_id;
            validGroupIds.add(r.group_id);
        }
    });

    const groupStats = {};
    saasGroups.forEach(g => {
        if (validGroupIds.has(g.id)) {
            let fallbackName = `Grupo ${g.id}`;
            if (intl) {
                fallbackName = intl.formatMessage(
                    {
                        id:
                            'page.robots.cleaning_report.group_comparison.group_name_fallback',
                    },
                    { id: g.id }
                );
            }

            groupStats[g.id] = {
                group_id: g.id,
                group_name: g.name || fallbackName,
                tasks: 0,
                time_seconds: 0,
                area_sqm: 0,
                frequency: 0,
                daysActive: new Set(),
            };
        }
    });

    list.forEach(task => {
        const robotId = task._robot && task._robot.id;
        const groupId = robotToGroup[robotId];

        if (groupId && groupStats[groupId]) {
            groupStats[groupId].tasks += 1;
            groupStats[groupId].time_seconds += Number(task.clean_time) || 0;
            groupStats[groupId].area_sqm += Number(task.clean_area) || 0;

            let dayStr = '';
            if (task.create_time) {
                const [datePart] = String(task.create_time).split('T');
                [dayStr] = datePart.split(' ');
            }
            if (dayStr) {
                groupStats[groupId].daysActive.add(dayStr);
            }
        }
    });

    return Object.values(groupStats).map(stat => {
        const freq = stat.daysActive.size;
        delete stat.daysActive;
        return {
            ...stat,
            frequency: freq,
        };
    });
}
